import { isEmpty } from 'lodash';
import get from 'lodash/get';
import React, {
  createContext,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import validator from 'validator';
import { mapItemTypeToBaseFormElement } from './defaults/baseFormElements';
import { mapItemTypeToContainerFormElement } from './defaults/containerFormElements';
import { getDependantTriggers, removeDependantFromTriggersMap } from './functions/dependants';
import { ItemsTypes } from './toolbar';

const validators = {
  email: {
    message: 'Value must be a valid email',
    validate: value => {
      return validator.isEmail(value);
    },
  },
  number: {
    message: 'Value must be a number',
    validate: value => {
      return validator.isInt(value) || validator.isFloat(value);
    },
  },
};

export const FormGeneratorContext = createContext({
  validators,
  invalidAnswers: [],
  unfulfilledRequiredItems: [],
  isValidated: false,
});

const mapItemTypeToElement = () => {
  return {
    ...mapItemTypeToBaseFormElement,
    ...mapItemTypeToContainerFormElement,
  };
};

const FormGenerator = ({
  items: rawItems,
  editing,
  onChange,
  onChangeItems,
  disabled,
  readOnly = true,
  registers,
  initAnswers,
  useInitAnswers,
  imperativeRef,
  isRegistersLoading,
  collapseThreeColumnFileUploadLayout = false,
}) => {
  const [answers, setAnswers] = useState(initAnswers || []);
  const [isValidated, setIsValidated] = useState(false);

  const resolveAnswers = useInitAnswers ? initAnswers : answers;

  const items = useMemo(() => {
    const dependants = Object.groupBy(
      rawItems.filter((item, i, arr) => item.providerId && arr.find(i => i.id === item.providerId)),
      ({ providerId }) => providerId,
    );
    let res = rawItems.filter(item => !item.providerId);

    while (!isEmpty(dependants)) {
      res = res.reduce((acc, curr) => {
        if (dependants[curr.id]) {
          const deps = dependants[curr.id];
          delete dependants[curr.id];
          return [...acc, curr, ...deps];
        }
        return [...acc, curr];
      }, []);
    }
    return res;
  }, [rawItems]);

  const handleSetAnswerData = useCallback(
    (name, id, value) => {
      let newAnswers = resolveAnswers;

      if (name === ItemsTypes.FileUpload) {
        // value === File[] | string[]
        const files = Array.isArray(value) ? value : [];

        // 1. remove all existing answers with this id
        const filtered = resolveAnswers.filter(a => a.id !== id);

        // 2. create new entries for each file
        const fileAnswers = files.map(file => ({
          id,
          name,
          value: file,
        }));

        newAnswers = [...filtered, ...fileAnswers];
      } else {
        // 🔹 DEFAULT logic (one answer per id)
        const answerExists = resolveAnswers.some(a => a.id === id);

        newAnswers = answerExists
          ? resolveAnswers.map(a => (a.id === id ? { ...a, value } : a))
          : [...resolveAnswers, { id, name, value }];
      }

      setAnswers(newAnswers);
      onChange?.(newAnswers);
    },
    [resolveAnswers, onChange],
  );

  // all items to show which are not children
  const mainItemsToShow = useMemo(
    () =>
      items.filter(item => {
        if (item.parentId) return false;

        const isDependant = !!item.providerId;

        if (!isDependant) return true;

        const provider = items.find(i => i.id === item.providerId);
        const providerData = provider && resolveAnswers.find(i => i.id === item.providerId);

        if (!providerData) return false;
        const activeTriggers = getDependantTriggers(provider, item.id);

        return Array.isArray(providerData.value)
          ? providerData.value.some(v => activeTriggers.includes(v))
          : activeTriggers.includes(providerData.value);
      }),
    [items, resolveAnswers],
  );

  // need for validation
  const allItemsToShow = useMemo(
    () =>
      items.filter(item => {
        const isChildren = !!item.parentId;
        const isDependant = !!item.providerId;

        if (!isChildren && !isDependant) return true;
        const parent = isChildren && items.find(i => i.id === item.parentId);

        if (parent && !parent?.providerId) return true;

        const providerId = parent?.providerId || item.providerId;

        const provider = items.find(i => i.id === providerId);
        const providerData = provider && resolveAnswers.find(i => i.id === providerId);

        if (!providerData) return false;
        const activeTriggers = getDependantTriggers(provider, item.id);

        return Array.isArray(providerData.value)
          ? providerData.value.some(v => activeTriggers.includes(v))
          : activeTriggers.includes(providerData.value);
      }),
    [items, resolveAnswers],
  );

  const unfulfilledRequiredItems = useMemo(
    () =>
      allItemsToShow.filter(item => {
        const itemData = resolveAnswers.find(data => data.id === get(item, 'id', 0));
        const isDataFulfilled =
          itemData?.value instanceof File || (itemData?.value && itemData?.value?.length);

        return get(item, 'required', false) && !isDataFulfilled;
      }),
    [allItemsToShow, resolveAnswers],
  );

  const invalidAnswers = useMemo(() => {
    return allItemsToShow.filter(item => {
      if (!item.validation || !(item.validation in validators)) return false;

      const answer = resolveAnswers.find(i => i.id === item.id);

      if (!answer?.value) return false;

      return !validators[item.validation].validate(answer?.value);
    });
  }, [allItemsToShow, resolveAnswers]);

  const handleDeleteItem = useCallback(
    id => {
      setAnswers(answers => {
        const newAnswers = answers.filter(i => i.id !== id);
        onChange && onChange(newAnswers);
        return newAnswers;
      });

      if (onChangeItems) {
        const itemToDelete = items.find(item => item.id === id);
        let newItems = items.filter(item => item.id !== id);

        if (itemToDelete?.providerId) {
          newItems = newItems.map(item => {
            if (item.id !== itemToDelete.providerId) return item;

            const dependantsIds = (item.dependantsIds || []).filter(
              dependantId => dependantId !== itemToDelete.id,
            );

            return {
              ...item,
              dependantsIds,
              ...removeDependantFromTriggersMap(item, itemToDelete.id, dependantsIds),
            };
          });
        }

        onChangeItems(newItems);
      }
    },
    [items, onChangeItems, onChange],
  );

  useImperativeHandle(
    imperativeRef,
    () => ({
      validate: () => {
        setIsValidated(true);
        const firstItem = unfulfilledRequiredItems[0] || invalidAnswers[0];

        if (firstItem) {
          const elem = document.querySelector(`[data-anchor="${firstItem.id}"]`);
          elem && elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return invalidAnswers.length || unfulfilledRequiredItems.length;
      },
    }),
    [invalidAnswers, unfulfilledRequiredItems],
  );

  // to clear answers which are not shown because of dependant provider selected options
  useEffect(() => {
    const notShowedItemsAnswers = resolveAnswers
      .filter(answer => !allItemsToShow.find(item => item.id === answer.id))
      .map(item => item.id);

    if (notShowedItemsAnswers.length) {
      const newAnswers = resolveAnswers.filter(
        answer => !notShowedItemsAnswers.includes(answer.id),
      );

      setAnswers(newAnswers);
      onChange && onChange(newAnswers);
    }
  }, [resolveAnswers, allItemsToShow, onChange]);

  const isFile = useCallback(
    id => items.find(a => a.id === id)?.type === ItemsTypes.FileUpload,
    [items],
  );

  return (
    <FormGeneratorContext.Provider
      value={{
        validators,
        invalidAnswers,
        unfulfilledRequiredItems,
        isValidated,
      }}
    >
      <div>
        <div className="react-form-builder-form">
          {mainItemsToShow.map(item => {
            const Element = mapItemTypeToElement()[item.type];

            if (!Element) return null;

            const itemValue =
              item.type === ItemsTypes.FileUpload
                ? resolveAnswers.filter(x => x.id === item.id)
                : resolveAnswers.find(x => x.id === item.id);

            return (
              <Element
                onDelete={handleDeleteItem}
                editing={editing}
                data={item}
                key={item.id}
                disabled={disabled}
                readOnly={readOnly}
                registers={registers}
                isRegistersLoading={isRegistersLoading}
                value={
                  item.type === ItemsTypes.FileUpload
                    ? itemValue?.map(x => x?.value)
                    : itemValue?.value
                }
                itemValue={itemValue}
                getItemValueById={id =>
                  isFile(id)
                    ? resolveAnswers.filter(x => x.id === id)
                    : resolveAnswers.find(x => x.id === id)
                }
                getDataById={id => items.find(x => x.id === id)}
                onChange={handleSetAnswerData}
                collapseThreeColumnFileUploadLayout={collapseThreeColumnFileUploadLayout}
              />
            );
          })}
        </div>
      </div>
    </FormGeneratorContext.Provider>
  );
};

export default FormGenerator;
